import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, Play } from 'lucide-react';

export default function TestimonialsSection() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      name: "Sarah Johnson",
      position: "Operations Manager",
      company: "TechCorp Industries",
      rating: 5,
      text: "The industrial printing solutions transformed our production line efficiency. The handjet printers are incredibly reliable and the support team is outstanding. We've seen a 40% improvement in our marking accuracy.",
      avatar: "SJ",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      name: "Michael Chen",
      position: "Store Manager",
      company: "RetailMax Chain",
      rating: 5,
      text: "Their EAS antenna system and electronic shelf labels revolutionized our store operations. Customer satisfaction increased significantly, and theft prevention is now seamless. Best investment we've made.",
      avatar: "MC",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      name: "Emma Rodriguez",
      position: "Manufacturing Director",
      company: "PackagePro Solutions",
      rating: 5,
      text: "The expiry date printing system is a game-changer for our packaging line. Zero errors, perfect print quality, and the implementation was surprisingly quick. Highly recommended for any manufacturing facility.",
      avatar: "ER",
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      name: "David Kim",
      position: "IT Director",
      company: "SmartRetail Group",
      rating: 5,
      text: "From barcode systems to POS integration, everything works flawlessly. The team's expertise and 24/7 support gives us complete confidence. Our checkout efficiency improved by 60%.",
      avatar: "DK",
      gradient: "from-amber-500 to-orange-500"
    },
    {
      name: "Lisa Thompson",
      position: "Quality Manager",
      company: "PrecisionMark Ltd",
      rating: 5,
      text: "Their coding and marking printers deliver consistent, high-quality results every time. The customization options perfectly match our specific requirements. Exceptional value and service.",
      avatar: "LT",
      gradient: "from-rose-500 to-red-500"
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonial(index);
    setIsAutoPlaying(false);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Animated background particles */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-1/4 w-2 h-2 bg-blue-400 rounded-full animate-ping opacity-20"></div>
        <div className="absolute top-1/3 right-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping opacity-30 delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping opacity-25 delay-2000"></div>
        <div className="absolute top-2/3 right-1/2 w-1 h-1 bg-amber-400 rounded-full animate-ping opacity-20 delay-3000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent mb-6">
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-poppins">
            Don't just take our word for it. Hear from the businesses that trust us 
            with their critical operations every day.
          </p>
        </div>

        {/* Main testimonial display */}
        <div className="max-w-5xl mx-auto mb-12">
          <div className="relative">
            {/* Main testimonial card */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 relative overflow-hidden group">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-emerald-100 to-cyan-100 rounded-full translate-y-12 -translate-x-12 opacity-40"></div>
              
              {/* Quote icon */}
              <div className="absolute top-6 left-6 w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center opacity-10">
                <Quote className="w-6 h-6 text-white" />
              </div>

              <div className="relative z-10">
                {/* Stars */}
                <div className="flex justify-center mb-6">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <Star 
                      key={i} 
                      className="w-6 h-6 text-amber-400 fill-current transform hover:scale-110 transition-transform duration-200"
                      style={{ animationDelay: `${i * 100}ms` }}
                    />
                  ))}
                </div>

                {/* Testimonial text */}
                <blockquote className="text-xl md:text-xl text-gray-700 leading-relaxed text-center mb-8 font-medium font-poppins">
                  "{testimonials[currentTestimonial].text}"
                </blockquote>

                {/* Author info */}
                <div className="flex items-center justify-center space-x-4">
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${testimonials[currentTestimonial].gradient} flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
                    {testimonials[currentTestimonial].avatar}
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-gray-900 text-lg">
                      {testimonials[currentTestimonial].name}
                    </div>
                    <div className="text-gray-600">
                      {testimonials[currentTestimonial].position}
                    </div>
                    <div className="text-blue-600 font-medium">
                      {testimonials[currentTestimonial].company}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center hover:scale-110 group"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center hover:scale-110 group"
            >
              <ChevronRight className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
            </button>
          </div>
        </div>

        {/* Testimonial dots navigation */}
        <div className="flex justify-center space-x-3 mb-12">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToTestimonial(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentTestimonial
                  ? 'bg-blue-600 w-8'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        {/* Thumbnail testimonials */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              onClick={() => goToTestimonial(index)}
              className={`cursor-pointer p-4 rounded-2xl transition-all duration-300 transform hover:scale-105 ${
                index === currentTestimonial
                  ? 'bg-white shadow-lg ring-2 ring-blue-500 ring-opacity-50'
                  : 'bg-white bg-opacity-50 hover:bg-opacity-80 hover:shadow-md'
              }`}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${testimonial.gradient} flex items-center justify-center text-white font-semibold text-sm`}>
                  {testimonial.avatar}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 text-sm truncate">
                    {testimonial.name}
                  </div>
                  <div className="text-gray-500 text-xs truncate">
                    {testimonial.company}
                  </div>
                </div>
              </div>
              <div className="flex mb-2">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 text-amber-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 text-xs leading-relaxed line-clamp-3">
                "{testimonial.text.substring(0, 80)}..."
              </p>
            </div>
          ))}
        </div>

        {/* Auto-play control */}
        {/* <div className="flex justify-center mt-8">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className={`flex items-center space-x-2 px-6 py-3 rounded-full transition-all duration-300 ${
              isAutoPlaying
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Play className={`w-4 h-4 ${isAutoPlaying ? 'animate-pulse' : ''}`} />
            <span className="text-sm font-medium">
              {isAutoPlaying ? 'Auto-playing' : 'Play Auto'}
            </span>
          </button>
        </div> */}

        {/* Bottom decoration */}
        {/* <div className="flex justify-center mt-16">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce delay-100"></div>
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce delay-200"></div>
          </div>
        </div> */}
      </div>

      <style jsx>{`
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out;
        }
      `}</style>
    </section>
  );
}